import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, tone = "dark", className = "" }) => (
  <div
    className={`mako-label flex items-center gap-3 ${
      tone === "dark" ? "text-[#0F4C81]" : "text-[#8AB4DA]"
    } ${className}`}
  >
    <span className="inline-block h-[1px] w-8 bg-current" />
    {children}
  </div>
);

export const SectionHead = ({ eyebrow, title, copy, tone = "dark", align = "left", className = "" }) => (
  <div
    className={`${align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"} ${className}`}
  >
    {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}
    <h2
      className={`mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-[3.25rem] ${
        tone === "dark" ? "text-[#111111]" : "text-white"
      }`}
    >
      {title}
    </h2>
    {copy && (
      <p
        className={`mt-6 max-w-2xl text-sm leading-relaxed sm:text-base ${
          tone === "dark" ? "text-[#3B3B3B]" : "text-white/70"
        } ${align === "center" ? "mx-auto" : ""}`}
      >
        {copy}
      </p>
    )}
  </div>
);

const baseBtn =
  "group inline-flex items-center justify-center gap-3 px-7 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.18em] mako-focus active:scale-[0.98] transition-[background-color,color,border-color,transform] duration-300";

export const Btn = ({ to, href, variant = "primary", children, className = "", ...rest }) => {
  const styles = {
    primary: "bg-[#0F4C81] text-white hover:bg-[#1463A5]",
    dark: "bg-[#111111] text-white hover:bg-[#3B3B3B]",
    outline:
      "border border-[#111111]/20 text-[#111111] hover:border-[#0F4C81] hover:text-[#0F4C81]",
    ghostLight:
      "border border-white/25 text-white hover:border-white hover:bg-white hover:text-[#111111]",
    white: "bg-white text-[#111111] hover:bg-[#F5F5F2]",
  }[variant];
  const cls = `${baseBtn} ${styles} ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (href)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} {...rest}>
        {inner}
      </a>
    );
  if (to)
    return (
      <Link to={to} className={cls} {...rest}>
        {inner}
      </Link>
    );
  return (
    <button type="button" className={cls} {...rest}>
      {inner}
    </button>
  );
};

export const ArrowLink = ({ to, children, tone = "dark", ...rest }) => (
  <Link
    to={to}
    className={`group inline-flex items-center gap-2 text-[0.6875rem] font-bold uppercase tracking-[0.2em] mako-focus ${
      tone === "dark"
        ? "text-[#111111] hover:text-[#0F4C81]"
        : "text-white hover:text-[#8AB4DA]"
    } transition-colors duration-300`}
    {...rest}
  >
    {children}
    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

export const Figure = ({ src, alt, ratio = "aspect-[4/3]", className = "", caption }) => (
  <figure className={className}>
    <div className={`relative overflow-hidden bg-[#F5F5F2] ${ratio}`}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-[1.04]"
      />
    </div>
    {caption && (
      <figcaption className="mako-label mt-4 text-[#7A7F85]">{caption}</figcaption>
    )}
  </figure>
);
